import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';
import Map from '../components/Map';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/settings/public`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    
    try {
      await axios.post(`${API_BASE_URL}/api/contact`, formData);
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: Phone, title: 'Phone', details: settings.phone || '+250 783 044 172', action: 'tel:+250783044172', color: 'from-orange-500 to-red-500' },
    { icon: Mail, title: 'Email', details: settings.email || 'info@hypamotors.com', action: 'mailto:info@hypamotors.com', color: 'from-blue-500 to-blue-600' },
    { icon: MapPin, title: 'Location', details: settings.address || 'Kigali, Rwanda', action: null, color: 'from-green-500 to-green-600' },
    { icon: Clock, title: 'Business Hours', details: 'Mon-Sun: 24/7 Support', action: null, color: 'from-purple-500 to-purple-600' },
  ];

  const faqs = [
    { q: 'Do you offer airport pickup?', a: 'Yes, we provide airport pickup and drop-off services at Kigali International Airport.' },
    { q: 'What documents do I need to rent a car?', a: 'You need a valid driver\'s license, passport or ID, and a credit card for deposit.' },
    { q: 'Is insurance included?', a: 'Yes, all our vehicles come with comprehensive insurance coverage.' },
    { q: 'Can I cancel my booking?', a: 'Yes, free cancellation up to 48 hours before your booking date.' },
  ];

  return (
    <div className="bg-dark-800 min-h-screen py-10">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Contact Us</h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Have questions about our vehicles or tours? We're here to help!
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info Cards */}
          <div className="lg:col-span-1 space-y-4">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="glass-card rounded-lg p-6 hover:shadow-elegant transition"
              >
                <div className="flex items-start space-x-4">
                  <div className={`bg-gradient-to-r ${info.color} p-3 rounded-full`}>
                    <info.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white mb-1">{info.title}</h3>
                    <p className="text-gray-400">{info.details}</p>
                    {info.action && (
                      <a href={info.action} className="text-orange-500 text-sm hover:underline mt-1 inline-block">
                        Contact →
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}

            {/* Map Component */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="glass-card rounded-lg overflow-hidden"
            >
              <Map />
            </motion.div>
          </div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2"
          >
            <div className="glass-card rounded-lg p-6 md:p-8">
              <h2 className="text-2xl font-bold text-white mb-6">Send Us a Message</h2>
              
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-green-400 mb-2">Message Sent!</h3>
                  <p className="text-gray-400 mb-6">
                    Thank you for contacting HYPA MOTORS. We'll get back to you within 24 hours.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition"
                  >
                    Send Another Message
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">Your Name *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full p-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white transition"
                        placeholder="Full Name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full p-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white transition"
                        placeholder="example@gmail.com"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full p-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white transition"
                        placeholder="+250 78X XXX XXX"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">Subject</label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full p-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white transition"
                        placeholder="Car Rental / Tour Inquiry"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-300">Message *</label>
                    <textarea
                      name="message"
                      required
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full p-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white transition resize-none"
                      placeholder="Tell us about your needs..."
                    ></textarea>
                  </div>
                  
                  {error && (
                    <div className="bg-red-500/20 border border-red-500 text-red-400 p-3 rounded-lg">
                      {error}
                    </div>
                  )}
                  
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-orange-500 text-white py-3 rounded-lg hover:bg-orange-600 transition font-semibold flex items-center justify-center space-x-2 disabled:opacity-50"
                  >
                    {submitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        <span>Send Message</span>
                      </>
                    )}
                  </button>
                  
                  <p className="text-xs text-gray-500 text-center mt-4">
                    * Required fields. We'll respond within 24 hours.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-16"
        >
          <h2 className="text-2xl font-bold text-white text-center mb-8">Frequently Asked Questions</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {faqs.map((faq, index) => (
              <div key={index} className="glass-card p-5 rounded-lg hover:shadow-elegant transition">
                <h3 className="font-bold text-orange-500 mb-2">{faq.q}</h3>
                <p className="text-gray-400 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;